// Browses the master catalog's Index Sheets of one Type: Tag (devices, the Device Spec tab) or Panel
// (panel specs, the Panel Spec tab). Sorted Preferred, Active, Do Not Use, Obsolete, Unreviewed; Do
// Not Use only with "Show all"; placeholders and off-grammar codes never show. From a row the user
// can start the Index Sheet Builder on that code's ISA type, manufacturer and group, or pull the
// sheet into this database (the pull also needs utilities.ds.project.catalog on this database).
import { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, ListPlus, Download } from 'lucide-react';
import { dsSpecApi, dsProjectCatalogApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import { applySearch, SEARCH_HINT } from '../../utils/listSearch';
import { statusBadgeClass } from './masterCatalog';
import { errText } from './deviceSpec';
import StuiSelect from '../../components/common/StuiSelect';

const SEARCH_FIELDS = ['indexSheet', 'description', 'manufacturer', 'manufacturerCode', 'model', 'specification', 'status'];

export default function SpecBrowser({ type, dbSlug, onOpenInBuilder }) {
  const { notify } = useNotifications();
  const { hasPermission, currentUser } = useAuth();
  const canPull = hasPermission('utilities.ds.project.catalog', dbSlug);
  const auditCfg = useMemo(() => ({ headers: { 'X-Changed-By': currentUser?.email || '' } }), [currentUser]);

  const [isa, setIsa] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [data, setData] = useState({ isaTypes: [], rows: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pulling, setPulling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dsSpecApi.getCatalog(type, isa, showAll);
      setData(res.data ?? { isaTypes: [], rows: [] });
    } catch (e) { notify.error(errText(e, 'Failed to load the catalog.')); }
    finally { setLoading(false); }
  }, [type, isa, showAll, notify]);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => applySearch(data.rows, search, SEARCH_FIELDS), [data.rows, search]);
  const noun = type === 'Panel' ? 'panel specs' : 'devices';

  const pull = async row => {
    setPulling(row.id);
    try {
      const res = await dsProjectCatalogApi.pullFromMaster(row.id, auditCfg, true);
      const r = res.data?.results?.[0];
      if (r?.status === 'pulled') notify.success(`${row.indexSheet} pulled into this database.`);
      else notify.warning(`${row.indexSheet}: ${r?.reason || 'not pulled.'}`);
      await load();
    } catch (e) { notify.error(errText(e, 'Pull failed.')); }
    finally { setPulling(null); }
  };

  return (
    <>
      <div className="stui-page-header">
        <div className="stui-row-8">
          <StuiSelect className="stui-field-input" value={isa} onChange={e => setIsa(e.target.value)} aria-label="ISA type">
            <option value="">All ISA types</option>
            {data.isaTypes.map(t => <option key={t.isa} value={t.isa}>{`${t.isa} (${t.count - (showAll ? 0 : t.hidden)})`}</option>)}
          </StuiSelect>
          <label className="stui-checkbox">
            <input type="checkbox" className="stui-checkbox-input" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            Show all (include Do Not Use)
          </label>
          <span className="stui-text-sm-muted">{shown.length === data.rows.length ? `${data.rows.length}` : `${shown.length} of ${data.rows.length}`}</span>
        </div>
        <div className="stui-row-8">
          <div className="stui-search-wrapper">
            <SearchClearIcon size={14} searchTerm={search} onClear={() => setSearch('')} />
            <input className="stui-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." title={SEARCH_HINT} />
          </div>
          <button className="stui-btn stui-btn-secondary" onClick={load} disabled={loading} title="Refresh"><RefreshCw size={14} /></button>
        </div>
      </div>
      <div className="stui-data-grid-container stui-scroll-cap stui-mt-sm">
        <div className="stui-data-grid-scroll-area">
          <table className="stui-data-grid stui-data-grid-auto">
            <thead>
              <tr><th>Index Sheet</th><th>Description</th><th>Mfr</th><th>Model</th><th>Specification</th><th>Status</th><th>Datasheet rev</th><th>This database</th><th /></tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={9} className="stui-td-empty">
                  {loading ? 'Loading…' : data.rows.length === 0 ? `The master catalog has no ${noun}${isa ? ` of type ${isa}` : ''} yet.` : 'No matching results'}
                </td></tr>
              )}
              {shown.map(r => (
                <tr key={r.id}>
                  <td className="stui-mono">{r.indexSheet}</td>
                  <td>{r.description}</td>
                  <td title={r.manufacturer || ''}>{r.manufacturerCode}</td>
                  <td>{r.model}</td>
                  <td>{r.specification}</td>
                  <td><span className={`stui-badge ${statusBadgeClass(r.status)}`}>{r.status || 'Active'}</span></td>
                  <td className="stui-mono" title={r.dsIndex || 'No datasheet'}>{r.rev ?? ''}</td>
                  <td>{r.inThisDatabase ? <span className="stui-badge stui-badge-green">In this database</span> : <span className="stui-td-muted">Not here</span>}</td>
                  <td>
                    <div className="stui-row-4">
                      <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => onOpenInBuilder(r)} title="Start a new Index Sheet on this code's ISA type, manufacturer and group">
                        <ListPlus size={14} /> Open in Builder
                      </button>
                      {canPull && (
                        <button className="stui-btn stui-btn-secondary stui-btn-sm" disabled={r.inThisDatabase || pulling != null}
                          onClick={() => pull(r)} title={r.inThisDatabase ? 'Already in this database' : 'Pull this sheet, its datasheet and values into this database'}>
                          <Download size={14} /> {pulling === r.id ? 'Pulling…' : 'Pull'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
